import { TagDescription } from "@reduxjs/toolkit/query";
import api from "src/shared/api";
import { IAddressFull } from "src/shared/types/address.types";
import {
  EQueryTagTypes,
  ISwoolBaseResponse,
  ISwoolResponse,
} from "src/shared/types/api";
import {
  ILicense,
  IPostLicenseDTO,
  IPutLicenseDTO,
} from "src/shared/types/companies.types";
import {
  IPartnerContactDTO,
  IPartnerDto,
} from "src/shared/types/partner.types";
import {
  IBankAccount,
  IPostBankAccountDTO,
  IPutBankAccountDTO,
} from "src/shared/types/partnerBankAccount";
import { getProvidesTags } from "src/shared/utils/url/query.helpers";
import {
  IPostPartnerAdressDTO,
  IPostPartnerContactDTO,
  IPostPartnerDTO,
  IPutPartnerDTO,
} from "../types";
import {
  INdaScanPostDTO,
  IPostLegalEntityCertificate,
  TGetPartnersListQuery,
} from "../types/partner";
import { IPutPartnerContactDTO } from "../types/partnerContact";

const partnersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPartnesList: build.query<
      ISwoolResponse<IPartnerDto[]>,
      TGetPartnersListQuery
    >({
      query: (params) => {
        return {
          url: "/api/partners/",
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response?.data) {
          return tags;
        }
        tags.push(...getProvidesTags(response?.data, EQueryTagTypes.PARTNERS), {
          type: EQueryTagTypes.PARTNERS,
          id: "LIST",
        });

        return tags;
      },
    }),
    postPartner: build.mutation<
      ISwoolBaseResponse<IPartnerDto>,
      IPostPartnerDTO
    >({
      query: (body) => {
        return {
          url: "/api/partners",
          method: "POST",
          body,
        };
      },
      invalidatesTags: () => [{ type: EQueryTagTypes.PARTNERS, id: "LIST" }],
    }),
    postNdaScan: build.mutation<ISwoolBaseResponse<void>, INdaScanPostDTO>({
      query: ({ partnerId, file, note, is_nda_signed }) => {
        const formData = new FormData();
        if (file) {
          formData.append("file", file);
        }
        formData.append("note", note);
        formData.append("is_nda_signed", String(is_nda_signed));

        return {
          url: `/api/partners/${partnerId}/nda`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: EQueryTagTypes.PARTNERS, id: arg.partnerId },
        { type: EQueryTagTypes.PARTNERS, id: "LIST" },
      ],
    }),
    postLegalEntityCertificate: build.mutation<
      ISwoolBaseResponse<void>,
      IPostLegalEntityCertificate
    >({
      query: ({ partnerId, file }) => {
        const formData = new FormData();
        if (file) {
          formData.append("file", file);
        }

        return {
          url: `api
/partners/${partnerId}/legal_certificate`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: EQueryTagTypes.PARTNERS, id: arg.partnerId },
        { type: EQueryTagTypes.PARTNERS, id: "LIST" },
      ],
    }),
    putPartner: build.mutation<ISwoolBaseResponse<IPartnerDto>, IPutPartnerDTO>(
      {
        query: ({ partner }) => {
          return {
            url: `/api/partners/${partner.id}`,
            method: "PUT",
            body: partner,
          };
        },
        invalidatesTags: (response, error) => {
          if (error) return [];

          return [{ type: EQueryTagTypes.PARTNERS, id: response?.data.id }];
        },
      },
    ),
    patchNdaScan: build.mutation<
      ISwoolBaseResponse<void>,
      Partial<INdaScanPostDTO>
    >({
      query: ({ partnerId, note, is_nda_signed }) => {
        return {
          url: `/api/partners/${partnerId}/nda`,
          method: "PATCH",
          body: { note, is_nda_signed },
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: EQueryTagTypes.PARTNERS, id: arg.partnerId },
        { type: EQueryTagTypes.PARTNERS, id: "LIST" },
      ],
    }),
    putLicense: build.mutation<
      ISwoolBaseResponse<ILicense>,
      { license: IPutLicenseDTO; partner_id: string }
    >({
      query: ({ partner_id, license }) => {
        const { id, ...rest } = license;

        return {
          url: `/api/partners/${partner_id}/license/${id}`,
          method: "PUT",
          body: rest,
        };
      },
      invalidatesTags: (response) => [
        { type: EQueryTagTypes.PARTNER_LICENSES, id: response?.data.id },
      ],
    }),
    postLicense: build.mutation<
      ISwoolBaseResponse<ILicense>,
      { license: IPostLicenseDTO; partner_id: string }
    >({
      query: ({ partner_id, license }) => {
        return {
          url: `/api/partners/${partner_id}/license`,
          method: "POST",
          body: license,
        };
      },
      invalidatesTags: (response) => [
        { type: EQueryTagTypes.PARTNERS, id: response?.data.partner_id },
      ],
    }),
    getPartnesAddressesList: build.query<
      IAddressFull[],
      { partner_id: string }
    >({
      query: ({ partner_id }) => {
        return {
          url: `/api/partners/${partner_id}/address`,
          method: "GET",
        };
      },
      transformResponse: (data: ISwoolBaseResponse<IAddressFull[]>) => {
        return data.data;
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response) {
          return tags;
        }
        tags.push(
          ...getProvidesTags(response, EQueryTagTypes.PARTNER_ADDRESSES),
          { type: EQueryTagTypes.PARTNER_ADDRESSES, id: "LIST" },
        );

        return tags;
      },
    }),
    putAddress: build.mutation<
      ISwoolBaseResponse<IAddressFull>,
      IPostPartnerAdressDTO
    >({
      query: ({ partnerId, address }) => {
        return {
          url: `/api/partners/${partnerId}/address/${address.id}`,
          method: "PUT",
          body: address,
        };
      },
      invalidatesTags: (response, _error, request) => {
        return response
          ? [
              { type: EQueryTagTypes.PARTNERS, id: request.partnerId },
              { type: EQueryTagTypes.PARTNER_ADDRESSES, id: "LIST" },
              {
                type: EQueryTagTypes.PARTNER_ADDRESSES,
                id: request.address.id,
              },
            ]
          : [];
      },
    }),
    postAddress: build.mutation<
      ISwoolBaseResponse<IAddressFull>,
      IPostPartnerAdressDTO
    >({
      query: ({ partnerId, address }) => {
        return {
          url: `/api/partners/${partnerId}/address`,
          method: "POST",
          body: address,
        };
      },
      invalidatesTags: (response, _error, request) => {
        return response
          ? [
              { type: EQueryTagTypes.PARTNERS, id: request.partnerId },
              { type: EQueryTagTypes.PARTNER_ADDRESSES, id: "LIST" },
            ]
          : [];
      },
    }),
    deleteAddress: build.mutation<
      void,
      { partnerId: string | number; addressId: string | number }
    >({
      query: ({ partnerId, addressId }) => {
        return {
          url: `/api/partners/${partnerId}/address/${addressId}`,
          method: "DELETE",
        };
      },

      invalidatesTags: (_response, _error, request) => {
        return [
          { type: EQueryTagTypes.PARTNERS, id: request.partnerId },
          {
            type: EQueryTagTypes.PARTNER_ADDRESSES,
            id: request.addressId,
          },
          { type: EQueryTagTypes.PARTNER_ADDRESSES, id: "LIST" },
        ];
      },
    }),
    getPartnerContact: build.query<
      IPartnerContactDTO,
      { partner_id: string; id: string }
    >({
      query: ({ partner_id, id }) => {
        return {
          url: `/api/partners/${partner_id}/contact_person/${id}`,
        };
      },
      transformResponse: (data: ISwoolBaseResponse<IPartnerContactDTO>) => {
        return data.data;
      },
      providesTags: (response) => {
        return [{ type: EQueryTagTypes.PARTNER_CONTACTS, id: response?.id }];
      },
    }),
    getPartnerContactsList: build.query<
      IPartnerContactDTO[],
      { partner_id: string }
    >({
      query: ({ partner_id }) => {
        return {
          url: `/api/partners/${partner_id}/contact_person`,
        };
      },
      transformResponse: (data: ISwoolBaseResponse<IPartnerContactDTO[]>) => {
        return data.data;
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response) {
          return tags;
        }
        tags.push(
          ...getProvidesTags(response, EQueryTagTypes.PARTNER_CONTACTS),
          { type: EQueryTagTypes.PARTNER_CONTACTS, id: "LIST" },
        );

        return tags;
      },
    }),
    putPartnerContact: build.mutation<
      ISwoolBaseResponse<IPartnerContactDTO>,
      IPutPartnerContactDTO
    >({
      query: ({ partner_id, id, ...rest }) => {
        return {
          url: `/api/partners/${partner_id}/contact_person/${id}`,
          method: "PUT",
          body: rest,
        };
      },
      invalidatesTags: (response) => {
        return response
          ? [
              { type: EQueryTagTypes.PARTNER_CONTACTS, id: response.data.id },
              { type: EQueryTagTypes.PARTNER_CONTACTS, id: "LIST" },
              {
                type: EQueryTagTypes.PARTNERS,
                id: response?.data.partner_id,
              },
            ]
          : [];
      },
    }),
    postPartnerContact: build.mutation<
      ISwoolBaseResponse<IPartnerContactDTO>,
      IPostPartnerContactDTO
    >({
      query: ({ partner_id, ...rest }) => {
        return {
          url: `/api/partners/${partner_id}/contact_person`,
          method: "POST",
          body: rest,
        };
      },
      invalidatesTags: (response) => {
        return response
          ? [
              { type: EQueryTagTypes.PARTNER_CONTACTS, id: response.data.id },
              { type: EQueryTagTypes.PARTNER_CONTACTS, id: "LIST" },
              {
                type: EQueryTagTypes.PARTNERS,
                id: response?.data.partner_id,
              },
            ]
          : [];
      },
    }),
    deletePartnerContact: build.mutation<
      void,
      { partnerId: string; contactId: string }
    >({
      query: ({ partnerId, contactId }) => {
        return {
          url: `/api/partners/${partnerId}/contact_person/${contactId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (response, _error, request) => [
        { type: EQueryTagTypes.PARTNER_CONTACTS, id: request.contactId },
        { type: EQueryTagTypes.PARTNER_CONTACTS, id: "LIST" },
        {
          type: EQueryTagTypes.PARTNERS,
          id: request?.partnerId,
        },
      ],
    }),
    getPartnerBankAccount: build.query<
      IBankAccount,
      { id: string; partner_id: string }
    >({
      query: ({ id, partner_id }) => {
        return {
          url: `/api/partners/${partner_id}/bank_account/${id}`,
          method: "GET",
        };
      },
      transformResponse: (data: ISwoolBaseResponse<IBankAccount>) => {
        return data.data;
      },
      providesTags: (response) => {
        return [
          { type: EQueryTagTypes.PARTNER_BANK_ACCOUNTS, id: response?.id },
        ];
      },
    }),

    putPartnerBankAccount: build.mutation<
      IBankAccount,
      { partner_id: string; bank_account: IPutBankAccountDTO }
    >({
      query: ({ partner_id, bank_account: body }) => {
        return {
          url: `/api/partners/${partner_id}/bank_account/${body.id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (response) => {
        return [
          { type: EQueryTagTypes.PARTNER_BANK_ACCOUNTS, id: response?.id },
        ];
      },
    }),
    postPartnerBankAccount: build.mutation<
      IBankAccount,
      { partner_id: string; bank_account: IPostBankAccountDTO }
    >({
      query: ({ partner_id, bank_account: body }) => {
        return {
          url: `/api/partners/${partner_id}/bank_account`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (response) => {
        return [{ type: EQueryTagTypes.PARTNERS, id: response?.partner_id }];
      },
    }),
  }),
});

export const {
  useGetPartnesListQuery,
  usePostPartnerMutation,
  usePostNdaScanMutation,
  usePostLegalEntityCertificateMutation,
  usePatchNdaScanMutation,
  usePutPartnerMutation,
  usePutPartnerContactMutation,
  usePostPartnerContactMutation,
  usePutLicenseMutation,
  usePostLicenseMutation,
  useGetPartnerContactQuery,
  usePutAddressMutation,
  useGetPartnesAddressesListQuery,
  usePostAddressMutation,
  useDeleteAddressMutation,
  useDeletePartnerContactMutation,
  useLazyGetPartnerContactsListQuery,
  useGetPartnerBankAccountQuery,
  usePutPartnerBankAccountMutation,
  usePostPartnerBankAccountMutation,
} = partnersApi;
